import type PaymentGateway from "@constants/PaymentGateway";
import type Product from "@interfaces/Product";
import {
  allowedGateways,
  defaultGlobalGateway,
} from "@utilities/paymentConfiguration";
import moment from "moment";
import { create } from "zustand";

export interface PaymentState {
  gateway: PaymentGateway;

  paymentIntentId: string;
  clientSecret: string;
  // Hosted-form URL handed back by redirect gateways (ArcoPay). Stripe confirms
  // client-side and leaves this empty.
  paymentUrl: string;
  productId: number | string;
  tempProduct: Product | null;
  total: number | string;
  currency: string;
  appliedCouponCode: string;
  showQRCode: boolean;

  isSmartTopUpChecked: boolean;
  scheduleDate: string;

  isCheckout: boolean;
  isOrdered: boolean;

  isAutoCheckout: boolean;

  setGateway: (value: PaymentGateway) => void;
  setPaymentIntentId: (value: string) => void;
  setClientSecret: (value: string) => void;
  setPaymentUrl: (value: string) => void;
  setProductId: (value: number | string) => void;
  setTempProduct: (value: Product) => void;
  setTotal: (value: number | string) => void;
  setCurrency: (value: string) => void;
  setAppliedCouponCode: (value: string) => void;
  setShowQRCode: (value: boolean) => void;
  setIsSmartTopUpChecked: (value: boolean) => void;
  setScheduleDate: (value: string) => void;
  setIsCheckout: (value: boolean) => void;
  setIsOrdered: (value: boolean) => void;
  setIsAutoCheckout: (value: boolean) => void;
  clear: () => void;
}

export const usePaymentStore = create<PaymentState>(set => ({
  gateway: defaultGlobalGateway,

  paymentIntentId: "",
  clientSecret: "",
  paymentUrl: "",
  productId: "",
  tempProduct: null,
  total: 0,
  currency: process.env.GATSBY_DEFAULT_CURRENCY ?? "EUR",
  appliedCouponCode: "",
  showQRCode: false,
  isSmartTopUpChecked: false,
  scheduleDate: moment().format("YYYY-MM-DD"),
  isCheckout: false,
  isOrdered: false,
  isAutoCheckout: false,
  setGateway: (value: PaymentGateway) => {
    if (!allowedGateways.includes(value)) {
      return;
    }
    set({
      gateway: value,
    });
  },
  setPaymentIntentId: (value: string) => {
    set({
      paymentIntentId: value,
    });
  },
  setClientSecret: (value: string) => {
    set({
      clientSecret: value,
    });
  },
  setPaymentUrl: (value: string) => {
    set({
      paymentUrl: value,
    });
  },
  setProductId: (value: number | string) => {
    set({
      productId: value,
    });
  },
  setTempProduct: (value: Product) => {
    set({
      tempProduct: value,
    });
  },
  setTotal: (value: number | string) => {
    set({
      total: value,
    });
  },
  setCurrency: (value: string) => {
    set({
      currency: value,
    });
  },
  setAppliedCouponCode: (value: string) => {
    set({
      appliedCouponCode: value,
    });
  },
  setShowQRCode: (value: boolean) => {
    set({
      showQRCode: value,
    });
  },
  setIsSmartTopUpChecked: (value: boolean) => {
    set({
      isSmartTopUpChecked: value,
    });
  },
  setScheduleDate: (value: string) => {
    set({
      scheduleDate: value,
    });
  },
  setIsCheckout: (value: boolean) => {
    set({
      isCheckout: value,
    });
  },
  setIsOrdered: (value: boolean) => {
    set({
      isOrdered: value,
    });
  },
  setIsAutoCheckout: (value: boolean) => {
    set({
      isAutoCheckout: value,
    });
  },
  clear: () => {
    set({
      paymentIntentId: "",
      clientSecret: "",
      paymentUrl: "",
      productId: "",
      tempProduct: null,
      total: 0,
      currency: process.env.GATSBY_DEFAULT_CURRENCY ?? "EUR",
      appliedCouponCode: "",
      showQRCode: false,
      isSmartTopUpChecked: false,
      scheduleDate: moment().format("YYYY-MM-DD"),
      isCheckout: false,
      isOrdered: false,
      isAutoCheckout: false,
    });
  },
}));

export const usePaymentState = (): PaymentState => {
  return usePaymentStore(state => {
    return state;
  });
};
